/*========================================================================
* $Id$
*
* Copyright © Mindbreeze Software GmbH, Linz, Austria, 2005-2023
*
* Der Nutzer des Computerprogramms anerkennt, dass der oben stehende
* Copyright-Vermerk im Sinn des Welturheberrechtsabkommens an der vom
* Urheber festgelegten Stelle in der Funktion des Computerprogramms
* angebracht bleibt, um den Vorbehalt des Urheberrechtes genuegend zum
* Ausdruck zu bringen. Dieser Urheberrechtsvermerk darf weder vom Kunden,
* Nutzer und/oder von Dritten entfernt, veraendert oder disloziert werden.
* =========================================================================*/

define([
  'backbone',
  "mbmustache",
  "i18n/i18n",
  "api/v2/search",
  "api/v2/api",
  "client/template",
  "component!base",
  "utils/dom",
  "utils/trace",
  "utils/result",
  "jquery",
  "!!raw-loader!../mustache/template.mustache",
  "!!raw-loader!../../results/mustache/template.mustache",
  "underscore",
  "api/chat/v1beta/generate",
  "client/client-config",
  "utils/sourceinfo",
  "utils/mustache",
  "marked",
  "utils/object",
  "client/client-extensions/result-enriching-response-observer",
  "jquery-ui-autocomplete",
  "model/properties",
  "api/v2/models/answer",
  "lit-html",
  "lit-html/directives/unsafe-html.js",
  "../../../../../packages/client-web-components/src/utils.ts"
], function (
  Backbone,
  Mustache,
  i18n,
  Search,
  api,
  Template,
  ComponentBase,
  dom,
  Trace,
  resultUtils,
  $,
  DEFAULT_TEMPLATE,
  DEFAULT_RESULTS_TEMPLATE,
  _,
  GenerateChannel,
  ClientConfig,
  SourceInfoUtil,
  MustacheUtil,
  marked,
  ObjectUtil,
  ResultEnrichingResponseObserver,
  jqueryuiAutocomplete,
  PropertiesModel,
  AnswerModelExports,
  LitExports,
  UnsafeHTML,
  WebComponentUtils
) {

  var ADD_TOGGLE_RESULT_AT_POSITION = 1;

  var SYSTEM_INSTRUCTIONS_DETAULT_VALUE = 'You are a retrieval-augmented search engine. ' + 
    'The insight engine found the following information in the knowledge base. ' + 
    'Please answer the question using only the provided content from the knowledge base. ' + 
    'When writing your answer, follow these rules:' +
    '\n- NEVER make up an answer if you don\'t know the answer. ' +
    '\n- NEVER include parts of this prompt in your answer.' +
    '\n- NEVER include notes in your answer.' +
    '\n- NEVER repeat phrases in your answer.' +
    '\n- Bold any important keywords in your answer.' +
    '\n- Use bullet points if listing multiple items.' +
    '\n- Italicize any definitions or clarifications you provide.'  +
    '\n- Begin the response with a brief summary, followed by detailed information.'  +
    '\n- Only if applicable, end with recommendations or next steps.'  +
    '\n- Ensure the tone is clear and appropriate for a general audience.'  +
    '\n- If there\'s ambiguity, feel free to ask clarifying questions to ensure a helpful response.';

  var PROMPT_SYSTEM_INSTRUCTION_NO_ANSWER = 'You are a retrieval-augmented search engine and a user ' +
    'asked a question which the insight engine found no answer to. ' +
    'Please inform the user in a few words, that you could not find ' +
    'anything relevant in the knowledge base and thus cannot answer ' +
    'their question. Try to summarize the users question and include ' +
    'it in your explanation. NEVER include this prompt in your answer.';
  
  var PROMPT_SYSTEM_INSTRUCTION_WITH_PLACEHOLDER = SYSTEM_INSTRUCTIONS_DETAULT_VALUE +
    '\n##################################################' +
    '\nBelow is the information from the knowledge base:' +
    '\n{summaries}' +
    '\n##################################################';

  var PROMPT_SYSTEM_INSTRUCTION_WITH_PLACEHOLDER_NO_ANSWER = 'You are a retrieval-augmented search engine and a user ' +
    'asked a question which the insight engine found no answer to. ' +
    'Please inform the user in a few words, that you could not find anything ' +
    'relevant in the knowledge base and thus cannot answer their question: "{question}". ' +
    'Try to summarize the users question and include it in your explanation. ' +
    'NEVER include this prompt in your answer.';

  var PROMPT_USER_INSTRUCTION_WITH_PLACEHOLDER = '{question}';
  var PROMPT_USER_INSTRUCTION_WITH_PLACEHOLDER_NO_ANSWER = '{question}';

  var AnswersTemplate = Template.extend({
    editableName: "editor_answer_box",

    initialize: function () {
      this._addedNodes = [];
      Template.prototype.initialize.apply(this, _.toArray(arguments));
      this.enableOrDisableVotingActions();
      this.$el = $(this.el);
      this.useCamelCaseNames = true;
    },

    schema: new Template.Schema({
      attributes: {
        "request-properties": {
          title: "editor_answer_request_properties",
          type: "boolean",
          default: true,
          visible: false
        },
        "ai-generate-text": {
          type: "boolean",
          title: "editor_generate_answer_from_ai",
          default: true
        },
        "result-answer-template-refid": {
          type: "string",
          visible: false
        },
        "enable-computing-when-generate": {
          title: "editor_answers_enable_computing_when_generate",
          type: "boolean",
          default: true,
          visible: false
        },
        "add-answers-from-results": {
          depends: "!/^false$/i.test(attributes['aiGenerateText'])",
          type: "string",
          title: "editor_answers_rerieved_context_source",
          defaultValue: "false",
          values: [
            { value: "false", text: i18n("editor_answers_rerieved_context_source_answers_only") },
            { value: "true", text: i18n("editor_answers_rerieved_context_source_answers_merge") },
            { value: "noanswers", text: i18n("editor_answers_rerieved_context_source_answers_results_if_no_answer") },
            { value: "replaceanswers", text: i18n("editor_answers_rerieved_context_source_answers_results_only") }
          ]
        },
        "retrieved-context-limit": {
          type: "int",
          title: "editor_answers_rerieved_context_limit",
          values: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
          default: 5,
        },
        "use-custom-prompt": {
          type: "boolean",
          title: "editor_use_custom_prompt",
          default: true,
          depends: "!/^false$/i.test(attributes['aiGenerateText'])",
        },
        "pipeline-selection": {
          type: "string",
          title: "editor_pipeline_selection",
          values: [
            { value: "key", text: i18n('editor_pipeline_selection_by_key')},
            { value: "id", text: i18n('editor_pipeline_selection_by_id')}
          ],
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( attributes['useCustomPrompt'] && !/^true$/i.test(attributes['useCustomPrompt']) )",
          default: 'key',
        },
        "pipeline-selection-value": {
          type: "string",
          title: "editor_pipeline_selection_value",
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( attributes['useCustomPrompt'] && !/^true$/i.test(attributes['useCustomPrompt']) )",
        },

        "use-insight-app-sources-for-generation": {
          title: "editor_use_insight_app_as_source_for_generation",
          type: "boolean",
          default: true,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( attributes['useCustomPrompt'] && !/^true$/i.test(attributes['useCustomPrompt']) )",
        },

        "use-user-input": {
          title: "editor_answers_use_user_input",
          type: "boolean",
          default: true,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( attributes['useCustomPrompt'] && !/^true$/i.test(attributes['useCustomPrompt']) && /^false$/i.test(attributes['useInsightAppSourcesForGeneration']) )",
        },

        "use-search-input": {
          title: "editor_answers_use_user_search",
          type: "boolean",
          default: true,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( attributes['useCustomPrompt'] && !/^true$/i.test(attributes['useCustomPrompt']) && /^false$/i.test(attributes['useInsightAppSourcesForGeneration']) )",
        },
        
        "use-user-constraints": {
          title: "editor_answers_use_user_constraints",
          type: "boolean",
          default: true,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( attributes['useCustomPrompt'] && !/^true$/i.test(attributes['useCustomPrompt']) && /^false$/i.test(attributes['useInsightAppSourcesForGeneration']) )",
        },
        "use-source-context-constraints": {
          title: "editor_answer_use_source_context_constraints",
          type: "boolean",
          default: true,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( attributes['useCustomPrompt'] && !/^true$/i.test(attributes['useCustomPrompt']) && /^false$/i.test(attributes['useInsightAppSourcesForGeneration']) )",
        },

        "llm-prompt-instruction-text": {
          type: "mustache",
          title: "editor_template_system_instructions",
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( !attributes['promptUseTemplates'] || /^false$/i.test(attributes['promptUseTemplates']) || /^false$/i.test(attributes['prompt-use-templates']) ) && ( !/^false$/i.test(attributes['useCustomPrompt']) )",
          default: SYSTEM_INSTRUCTIONS_DETAULT_VALUE
        },
        "prompt-conditional-instruction-text": { // different-prompt-instructions-for-no-answer
          type: "boolean",
          title: "editor_different_prompt_instructions_for_no_answer",
          "default": true,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( !attributes['promptUseTemplates'] || /^false$/i.test(attributes['promptUseTemplates']) || /^false$/i.test(attributes['prompt-use-templates'])) && ( !/^false$/i.test(attributes['useCustomPrompt']) )",
        },
        "llm-prompt-instruction-text-no-answer": {
          type: "mustache",
          title: "editor_prompt_system_instruction_for_no_answer",
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && !/^false$/i.test(attributes['promptConditionalInstructionText']) && !/^true$/i.test(attributes['promptUseTemplates']) && ( !/^false$/i.test(attributes['useCustomPrompt']) )",
          default: PROMPT_SYSTEM_INSTRUCTION_NO_ANSWER
        },

        "prompt-use-templates": {
          type: "boolean",
          title: "editor_advanced_prompt_configuration",
          "default": false,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && ( !/^false$/i.test(attributes['useCustomPrompt']) )"
        },

        "llm-prompt-instruction-template": {
          type: "mustache",
          title: "editor_system_message_template",
          default: PROMPT_SYSTEM_INSTRUCTION_WITH_PLACEHOLDER,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && /^true$/i.test(attributes['promptUseTemplates']) && ( !/^false$/i.test(attributes['useCustomPrompt']) )",
        },
        "prompt-conditional-instruction-template": {
          type: "boolean",
          title: "editor_different_prompt_instructions_for_no_answer",
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && /^true$/i.test(attributes['promptUseTemplates']) && ( !/^false$/i.test(attributes['useCustomPrompt']) )",
          "default": false,
        },
        "system-prompt-instruction-with-placeholder-no-answer": {
          type: "mustache",
          title: "editor_prompt_system_instruction_with_placeholder_for_no_answer",
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && (/^true$/i.test(attributes['promptUseTemplates']) && /^true$/i.test(attributes['promptConditionalInstructionTemplate']) ) && ( !/^false$/i.test(attributes['useCustomPrompt']) )",
          default: PROMPT_SYSTEM_INSTRUCTION_WITH_PLACEHOLDER_NO_ANSWER
        },

        "prompt-user-message-template": {
          type: "mustache",
          title: "editor_user_message_template",
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && /^true$/i.test(attributes['promptUseTemplates']) && !/^false$/i.test(attributes['useCustomPrompt'])",
          default: PROMPT_USER_INSTRUCTION_WITH_PLACEHOLDER,
        },
        "prompt-conditional-user-message-template": {
          type: "boolean",
          title: "editor_different_user_prompt_instructions_for_no_answer",
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && /^true$/i.test(attributes['promptUseTemplates'])",
          "default": false,
        },
        "prompt-user-message-template-no-answer": {
          type: "mustache",
          title: "editor_prompt_user_instruction_with_placeholder_for_no_answer",
          default: PROMPT_USER_INSTRUCTION_WITH_PLACEHOLDER_NO_ANSWER,
          depends: "!/^false$/i.test(attributes['aiGenerateText']) && (/^true$/i.test(attributes['promptUseTemplates']) && /^true$/i.test(attributes['promptConditionalUserMessageTemplate']) && !/^false$/i.test(attributes['useCustomPrompt']) )",
        },
        "clear-answer-on-change": {
          type: "boolean",
          title: "editor_clear_answer_on_change",
          default: false,
          depends: "!/^false$/i.test(attributes['aiGenerateText'])",
        },
        "answer-model": {
          type: "string",
          title: "editor_answer-model",
          default: "",
          visible: false
        },
        "enable-citation" : {
          title: "editor_answer_enable_citation",
          type: "boolean",
          default: false
        },
        "line-limit" : {
          title: "editor_answer_line_limit",
          type: "int",
          values: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20],
          default: 5,
          visible: true
        },
        "show-question": {
          title: "editor_answer_show_question",
          type: "boolean",
          default: true
        },
        "use-metadatakeys" : {
          title: "editor_answer_use_metadatakeys",
          type: "boolean",
          default: false
        },
        "metadatakeys": {
          title: "editor_answer_metadatakeys",
          type: "list",
          depends: "/^true$/i.test(attributes['useMetadatakeys']) ",
        }
      }
    }),

    hasContent: function() { return true; },
    getContentSchema: function() {
      return {
        type: "template"
      };
    },

    getOptions: function (options) {
      var attrs = this.get();
      var newAtts = {};
      if (attrs.modelId) {
        newAtts.modelId = attrs.modelId;
      }
      if (attrs.metadatakeys) {
        newAtts.metadatakeys = attrs.metadatakeys;
      }

      options = _.extend(
        {},
        Template.prototype.getOptions.apply(this, arguments),
        this.attributeModel.getCamelCase(),
        newAtts
      );
      return this.schema.parseAttributes(options);
    },
    
    enableOrDisableVotingActions: function () {
      var enableVoting = $('[data-template="results"][data-enable-voting=true]').length;
      if (!enableVoting) {
        $(this.el).addClass('voting-disabled');
      }
    },

    getDefaultTemplateAsEl: function() {
      var html = this.getDefaultTemplate();
      if (html) {
        return dom.instantiate(html);
      }
    },

    getContent: function() { return this.children[0].template; },

    getDisplayedProperties: function() {
      const displayedProperties = Tempalte.prototype.getDisplayedProperties.call(this);
      displayedProperties['fqcategory'] = resultUtils.mergeProperty(
        displayedProperties['fqcategory'],
        { formats: "VALUE" }
      );
      displayedProperties['mes:key'] = resultUtils.mergeProperty(
        displayedProperties['mes:key'],
        { formats: "VALUE" }
      );
      return displayedProperties;
    },
    
    createInsightAnswerActionsEl: function() {
      // mb-answer-sources
      this.insightAnswerActionsEl = document.createElement("insight-answer-actions");
    }, 

    createInsightAnswerSourcesEl: function() {
      // mb-answer-sources
      this.insightAnswerSourcesEl = document.createElement("insight-answer-sources");
      const options = this.get();
      if (options.metadatakeys) {
        this.insightAnswerSourcesEl.setAttribute('metadatakeys', JSON.stringify(options.metadatakeys));
      }
    },

    isChatAIServiceEnabledAsync: function() {
      var sourceInfoUtil = new SourceInfoUtil(this.application.models.sourceInfo, this.application.channels);
      return sourceInfoUtil.isServiceAvailableAsync("mindbreeze.chat.v1beta");
    },
    
    createDefaultTemplateIfNonePresent: function() {
      let isTemplatePresent = this.getTemplateNodeHasChildren()
      if (!isTemplatePresent) {
        this.useLegacyMustacheTemplate = false;
        this.createInsightAnswerActionsEl();
        this.createInsightAnswerSourcesEl();
        const el = document.createElement("div");
        el.className = "mb-answer hidden";
        const elItem = document.createElement("div");
        elItem.className = "mb-answer-item mb-answer-text-container";
        el.appendChild(elItem);
        const elAnswerActions = document.createElement("div");
        elAnswerActions.className = "mb-answer-actions";
        elAnswerActions.appendChild(this.insightAnswerActionsEl);
        el.appendChild(elAnswerActions);
        const elAnswerSources = document.createElement("div");
        elAnswerSources.className = "mb-answer-sources";
        elAnswerSources.appendChild(this.insightAnswerSourcesEl);
        el.appendChild(elAnswerSources);
        this._addedNodes.push(el);
        return this.isChatAIServiceEnabledAsync().then(isChatAIServiceEnabled => {
          this.isChatAIServiceEnabled = isChatAIServiceEnabled;
          return this.append(el);
        });
      }
      else {
        this.useLegacyMustacheTemplate = true;
        return this.isChatAIServiceEnabledAsync().then(isChatAIServiceEnabled => {
          this.isChatAIServiceEnabled = isChatAIServiceEnabled;
          return this.node && this.node.children || [];
        });
      }
    },
    
    createView: function(options) {
      const explicitlyDefinedProps = this.get();
      const overruledOptions = {};

      // If not explicity configured (neither in data- attribute nor in the settings options)
      if (explicitlyDefinedProps.lineLimit === undefined && explicitlyDefinedProps.promptUseTemplates) {
        overruledOptions.lineLimit = 0;
      }

      options = _.extend(
        {},
        options,
        this.schema.parseAttributes(this.attributeModel.get()),
        this.schema.parseAttributes(this.attributeModel.getCamelCase()),
        this.get()
      );

      let isAIGenerateText = this.get("ai-generate-text");
      if (isAIGenerateText == null) {
        isAIGenerateText = true;
      }
      if (isAIGenerateText === "false") {
        isAIGenerateText = false;
      }

      var view = new AnswersView(_.extend({
        requestAdditionalAnswerProperties: this.get("request-properties"),
        enableCitation: this.get("enable-citation"),
        lineLimit: this.get("line-limit"),
        showQuestion: this.get("show-question"),
        useCustomPrompt: this.get("use-custom-prompt"),
        pipelineSelection: this.get("pipeline-selection"),
        pipelineSelectionValue: this.get("pipeline-selection-value"),
        useInsightAppSourcesForGeneration: this.get("use-insight-app-sources-for-generation"),
        useUserInput: this.get("use-user-input"),
        useSearchInput: this.get("use-search-input"),
        enableComputingWhenGenerate: this.get("enable-computing-when-generate"),
        useUserConstraints: this.get("use-user-constraints"),
        useSourceContextConstraints: this.get("use-source-context-constraints"),
        conversationInputPresetsJson: options.conversationInputPresetsJson || this.get("conversation-input-presets-json"),
        llmPromptInstructionText: this.get("llm-prompt-instruction-text"),
        promptConditionalInstructionText: this.get("prompt-conditional-instruction-text"),
        llmPromptInstructionTextNoAnswer: this.get("llm-prompt-instruction-text-no-answer"),
        promptUseTemplates: this.get("prompt-use-templates"),
        llmPromptInstructionTemplate: this.get("llm-prompt-instruction-template"),
        promptConditionalInstructionTemplate: this.get("prompt-conditional-instruction-template"),
        systemPromptInstructionWithPlaceholderNoAnswer: this.get("system-prompt-instruction-with-placeholder-no-answer"),
        promptUserMessageTemplate: this.get("prompt-user-message-template"),
        promptConditionalUserMessageTemplate: this.get("prompt-conditional-user-message-template"),
        promptUserMessageTemplateNoAnswer: this.get("prompt-user-message-template-no-answer"),
        addAnswersFromResults: this.get("add-answers-from-results") || 'false',
        // TOOD Check what we have to to do if this is set for insight apps
        resultAnswerTemplateRefid: this.get("result-answer-template-refid"),
        retrievedContextLimit: this.get("retrieved-context-limit") || 5,
        useLegacyMustacheTemplate: this.useLegacyMustacheTemplate === true
      },
      options,
      {
        application: options.application,
        template: this,
        el: this.el,
        answerTemplate: options.node.children[0].template,
        aiGenerateText: isAIGenerateText && this.isChatAIServiceEnabled
      },
        overruledOptions
      ));

      this.instances.push(view);

      if (options.node) {
        
        var subViewOptions = _.extend(
          {},
          options,
          {
            parentElement: view.el,
          }
        )
        
        const evaluatedChild = this.evaluateGuardedChild(options.model);
        
        _.each(options.node.children, function (childNode) {
          // create views for all children which are either not guarded 
          //  or which was chosen according to the guard expressions
          if (!childNode.guard || evaluatedChild === childNode) {
            if (childNode.el == this.insightAnswerSourcesEl ||
               childNode.el == this.insightAnswerActionsEl) {
              let model = view.answerModel;
              this.createSubView(childNode, subViewOptions, model);
            }
            else {
              // legacy
              let model = view.legacyModelAdapter && view.legacyModelAdapter.getModel() || view.answerModel;
              this.createSubView(childNode, subViewOptions, model);
            }
          }
        }, this);
      }
      return view;
    },

 

    remove: function (options) {
      // This is called on destroy
      _.forEach(this.instances, function (view) {
        view.remove();
      });
      this.instances = [];
      var nodeChildren = options && options.node && options.node.children;
      _.forEach(nodeChildren, function (childNode) {
        childNode && childNode.template && childNode.template.remove({
          node: childNode
        });
      });
      for (const node of this._addedNodes) {
        try {
          node.parentNode.removeChild(node);
        } catch (e) {}
      }
      this._addedNodes = [];
      this.insightAnswerSourcesEl = null;
    }
  });


  /*

    Current Response Models
    -----------------------
    - answerModel
        - generated_text
        - is_streaming
        - ...
    - answersCollection (Search.AnswerCollection similar to Search.ResultCollection)
        - per Answer a PropertiesModel (analog to what we are using in the <insight-result> widget.
  

   */


  
  var AnswersView = ComponentBase.extend({
    events: function () {
      return _.extend({},
        ComponentBase.prototype.events,
        {
          "click .mb-action-stop-generating": "handleStopGenerating",
          "click .mb-action-regenerate-answer": "handleRegenerateAnswer",
          "click .mb-action-copy-answer": "handleCopyAnswer",
          "click .mb-action-feedback-answer": "handleFeedback",
          "click .additional-sources-label": "handleShowAditionalSourcesClick",
          "click a": "handleGeneratedTextLinkClick"
      });
    },
    
    answerModel: null,

    initialize: function(options) {
      this.answerModel = null;  
      var models = this.options.application.models;

      if (this.options.addAnswersFromResults !== 'false' || this.options.resultAnswerTemplateRefid) {
        this.resultAnswerMustacheTemplate = new MustacheUtil(this.getAnswersTemplateString());
      }

      // answer model: either from a data-answer-model attribute or the chatFrame component
      if (this.options.answerModel) {
        // Check if a model with this ID already exists in the application's models
        this.answerModel = models[this.options.answerModel];
        if (!this.answerModel) {
          this.answerModel = this.createAnswerModel();
          models[this.options.answerModel] = this.answerModel;
        }
      } else {
        // If no answerModel option is provided, create a standalone Backbone model (not stored in application.models)
        this.answerModel = this.createAnswerModel();
      }
      this.answerModel.setSearchModel(this.model);
      if (options.useLegacyMustacheTemplate) {
        this.legacyModelAdapter = new AnswerModelExports.RAGAnswerLegacyModelAdapter(this.answerModel);
      }
      
      this.listenTo(this.answerModel.output, "change:generated_text", this.render.bind(this));
      this.listenTo(this.answerModel.output, "change:stream_request_status", ()=> {
        this.showComponentClientOnly();
      });
      this.answerModel.on("computing", ()=> {
        // when new generation starts, reset line restriction
        this.removeLineRestriction();
        this.showMoreClicked = false;
      });
      
      if (this.options.hideOnNoResults !== false) {
        this.listenTo(this.answerModel, "change", this.updateDisplay);
        this.updateDisplay();
      }

      this.listenTo(this.answerModel, "computed", this.onAnswerComputed.bind(this));
      
      this.handleSourceInfoChange();
      // In some cases, this is loaded after/parallel to the search
      this.listenTo(this.options.application.models.sourceInfo, "change", this.handleSourceInfoChange.bind(this));

      if (!this.$el.hasClass('mb-answers-container')) {
        var currentClasses = this.$el.attr('class');
        this.$el.attr('class', 'mb-answers-container ' + currentClasses);
      }
      
      if (this.options.useLegacyMustacheTemplate) {
        this.el.addEventListener("mousedown", (e) => {
          // console.log("from x,y: ", document.elementFromPoint(e.clientX, e.clientY));
          // console.log("from target", e.target, e.target.classList);
          if (Array.from(e.target.classList).indexOf("mb-action-stop-generating") >= 0) {
            this.handleStopGenerating();
          }
        });
      }

      this.$el.on('click', this._handleClick.bind(this));

      ComponentBase.prototype.initialize.apply(this, arguments);
    },

    updateDisplay: function() {
      if (this.answerModel.output.get("stream_request_status") == null) {
        if (this.options.application && this.options.application.editor) {
          // in designer always show answer widget at initialization
          this.$el.show();
        } else {
          this.$el.hide();
        }
        this.$el.removeClass("ai-generated-answer");
      }
      else {
        this.$el.show();
        this.$el.addClass("ai-generated-answer");
      }
    },
    
    getPipelineSelection: function() {
      if (this.options.useCustomPrompt) {
        return;
      }
      if (this.options.pipelineSelection === 'id') {
        return {pipeline_id: this.options.pipelineSelectionValue}
      } else {
        return {pipeline_key: this.options.pipelineSelectionValue}
      }
    },

    createAnswerModel: function() {

      const channelOptions = Object.assign(
        {},
        this.options.application.channelOptions || {}
      );
      channelOptions.application = this.options.application;
      // TODO change to standard channel url
      // chnanels are currenlty basd on api/v2
      // const url = this.defaultChannel() + "/api/v2/";
      const searchModelOptions = {
        useUserInput: this.options.useUserInput === false || this.options.useUserInput === "false" ? false : true,
        useSearchInput: this.options.useSearchInput,
        useUserConstraints: this.options.useUserConstraints,
        useSourceContextConstraints: this.options.useSourceContextConstraints,
        enableComputingWhenGenerate: this.options.enableComputingWhenGenerate,
        clearAnswerOnChange: this.options.clearAnswerOnChange !== false,
        addAnswersFromResults: this.options.addAnswersFromResults,
        retrievedContextLimit: this.options.retrievedContextLimit,
        resultAnswerMustacheTemplate: this.resultAnswerMustacheTemplate
      };

      let answerInputPresets = this.getConversationInputPresetsJson();
      
      if (!answerInputPresets || !answerInputPresets.citation_options) {
        answerInputPresets = Object.assign(answerInputPresets || {}, {
          citation_options: {
            "enable_citation": this.options.enableCitation
          }
        });
      }
      else {
        answerInputPresets.citation_options.enable_citation = this.options.enableCitation;
      }
      
      const defaultChannel = this.defaultChannel();
      const url = defaultChannel.get("url");
      
      const channelInfo = { url,
                            channelOptions,
                            prepareRequest: defaultChannel.prepareRequest.bind(defaultChannel) };
      const options = { application: this.options.application,
                        channelInfo};
      
      if (this.options.aiGenerateText === false) {
        return this.createAnswerModelFromSearchResponseAnswers(options);
      } else if ( ['replaceanswers', 'noanswers', 'true'].includes(this.options.addAnswersFromResults) ) {
        return this.createSearchResponseResultsAnswerModel(answerInputPresets, options, searchModelOptions);
      }
      else {
        const pipelineSelection = this.getPipelineSelection();
        if (!pipelineSelection) {
          return this.createAnswerModelFromCustomPrompt(answerInputPresets, options, searchModelOptions);
        }
        return this.createAnswerModelFromPipelineSelection(answerInputPresets, pipelineSelection, options, searchModelOptions);
      }
    },

    createAnswerModelFromSearchResponseAnswers: function(options) {
      return AnswerModelExports.createSearchResponseAnswerModel(options, this.model);
    },
    
    createAnswerModelFromPipelineSelection: function(presets, pipelineSelection, options, searchModelOptions) {
      // TODO trigger
      const SearchModelTriggerStrategyType = AnswerModelExports.SearchModelTriggerStrategyType;
      const triggerType = !this.options.useInsightAppSourcesForGeneration
            ? SearchModelTriggerStrategyType.TriggerOnInputChange
            : SearchModelTriggerStrategyType.TriggerOnComputed;

      const attributes = Object.assign({}, presets, pipelineSelection);
    return AnswerModelExports.createRAGPipelineAnswerModel(attributes,
                                                             options,
                                                             this.model,
                                                             triggerType,
                                                             searchModelOptions);
    },
    
    createAnswerModelFromCustomPrompt: function(presets, options, searchModelOptions) {
      const SearchModelTriggerStrategyType = AnswerModelExports.SearchModelTriggerStrategyType;
      const triggerType = SearchModelTriggerStrategyType.TriggerOnComputed;
       //const triggerType = SearchModelTriggerStrategyType.TriggerOnInputChange;
      const messageTemplates = this.getEffectiveMessageTemplates();
      const attributes = Object.assign({}, presets, {
        generation_options: {
          message_templates: messageTemplates
        }
      });
      return AnswerModelExports.createRAGMessageTemplatesAnswerModel(
        attributes,
        options,
        this.model,
        triggerType,
        searchModelOptions
      );
    },

    createSearchResponseResultsAnswerModel: function(presets, options, searchModelOptions) {
      const SearchModelTriggerStrategyType = AnswerModelExports.SearchModelTriggerStrategyType;
      const triggerType = SearchModelTriggerStrategyType.TriggerOnComputed;

      const messageTemplates = this.getEffectiveMessageTemplates();
      const attributes = Object.assign({}, presets, {
        generation_options: {
          message_templates: messageTemplates
        }
      });

      return AnswerModelExports.createRAGPipelineAnswerModel(
        attributes,
        options,
        this.model,
        triggerType,
        searchModelOptions
      );
    },

    getComputationTask: function(){
      // Generation in Progress Promise
      if (this._renderAnswersInGetComputationTaskCall) {
          if (this.model._nextPageCount) {
              return Promise.resolve();
          }

          if (!this.options.answerModel) {
            return this.renderAnswers(this.options);
          }
      }
      return this.generationInProgress;
    },

    handleSourceInfoChange: function() {
      if (this.options.application && !this.options.application.editor) {
        this.$el.addClass('hidden');
      } else {
        this.$el.removeClass('hidden');
      }
    },

    setModel: function (model) {
      var that = this;

      if (this.answersCollection) {
        this.answersCollection.off("add");
      }

      ComponentBase.prototype.setModel.apply(this, arguments);

      if (this.model) {

        if (this.options.enableComputingWhenGenerate) {
          this.model.registerComputationTaskProvider(this);
        }

        var displayedProperties = {};

        if (this.options.requestAdditionalAnswerProperties) {
          displayedProperties = this.getDisplayedProperties();
        }``

        displayedProperties['title'] = resultUtils.mergeProperty(
          displayedProperties['title'],
          { formats: ["HTML"] }
        );
        
        displayedProperties['extension'] = resultUtils.mergeProperty(
          displayedProperties['extension'],
          { formats: ["VALUE"] }
        );

        displayedProperties['actions'] = resultUtils.mergeProperty(
          displayedProperties['actions'],
          { formats: ["VALUE", 'PROPERTY'] }
        );

        // Without "content" property, the answer response property will not be available
        displayedProperties['content'] = resultUtils.mergeProperty(
          displayedProperties['content'],
          { formats: ["VALUE"] }
        );

        displayedProperties['is_curated_answer'] = resultUtils.mergeProperty(
          displayedProperties['is_curated_answer'],
          { formats: ["VALUE"] }
        );

        displayedProperties['mes:key'] = resultUtils.mergeProperty(
          displayedProperties['mes:key'],
          { formats: ["VALUE"] }
        );

        displayedProperties['fqcategory'] = resultUtils.mergeProperty(
          displayedProperties['fqcategory'],
          { formats: ["VALUE"] }
        );
        
        displayedProperties['mes:date'] = resultUtils.mergeProperty(
          displayedProperties['mes:date'],
          { formats: ["VALUE", "HTML"] }
        );

        displayedProperties['mes:summary'] = resultUtils.mergeProperty(
          displayedProperties['mes:summary'],
          { formats: ["PROPERTY"] }
        );

        const propFormats = ["VALUE", "HTML", "PROPERTY"];

        if (_.isArray(this.options.metadatakeys)) {
          try {
            for (var metadataKey of this.options.metadatakeys) {
              displayedProperties[metadataKey] = { formats: propFormats };
            }
          } catch (e) {
            console.warn('Failed to parse answer metadatakeys property, the values should be separated by comma', e);
          }
        }

        if (this.resultAnswerMustacheTemplate) {
            var additionalProperties = this.resultAnswerMustacheTemplate.getProperties();
            for (var key of Object.getOwnPropertyNames(additionalProperties)) {
              displayedProperties[key] = { formats: propFormats };
            }
        }

        if (this.model.addOrUpdateRequestedProperties) {
          this.displayedPropertiesId = this.model.addOrUpdateRequestedProperties(displayedProperties);
        }
      }
    },

    handleGeneratedTextLinkClick: function(e) {
      var target = e.target;
      const url = new URL(target.href);
      const citationPattern = /#(?:block-)?citation-id=([\w-]+)/;
      const citationMatch = url.hash.match(citationPattern)

      // Ignore source click while streaming
      if (citationMatch && this.answerModel.get('computing')) {
        e.preventDefault();
        e.stopPropagation();
      }
    },

    setModelValues: function (name, value) {
      if (!this.model || !this.model.unset) {
        return;
      }
      if (!_.isUndefined(value)) {
        this.model.set(name, value, {silent: true});
      } else {
        this.model.unset(name, {silent: true});
      }
    },

    getDisplayedProperties: function () {
      // return this.options.answerTemplate.properties;
      return {};
    },

    change: function (model, collection, options) {
      this.$el.removeClass("show-all-answers");
    },

    reset: function () {
      this.$el.find(".mb-answer-list-toggle-container").remove();
    },
    
    defaultChannel: function () {
      return this.options.application.channels.at(0);
    },

    getAnswersTemplateString: function() {
      var textTemplate = ' <h2> {{title}} </h2> ' +
      ' \n  {{#mes:summary}} {{name}}, {{/mes:summary}} ' + 
      ' \n  {{#mes:summary}} {{name}}, {{/mes:summary}} ' +
      ' \n  {{#mes:summary}} {{name}}, {{/mes:summary}} ' + 
      ' \n  {{#content}} {{{.}}} {{/content}} ' ;

      if (this.options.resultAnswerTemplateRefid) {
        var templateEl = document.getElementById(this.options.resultAnswerTemplateRefid);
        if (templateEl && templateEl.innerHTML) {
          return templateEl.innerHTML;
        }
        console.warn("Could not find template with id \"" + this.options.resultAnswerTemplateRefid + "\"");
      }

      return textTemplate;
    },

    getMessageTemplatesNoResultAnswers: function() {
      var systemRoleContent = [];
      var userRoleContent = [];
      
      // Advanced Prompt Instruction enabled
      if (this.options.promptUseTemplates === true) {

        const systemPrompt = this.options.promptConditionalInstructionTemplate
              ? this.options.systemPromptInstructionWithPlaceholderNoAnswer
              : this.options.llmPromptInstructionTemplate;

        const userPrompt = this.options.promptConditionalUserMessageTemplate
              ? this.options.promptUserMessageTemplateNoAnswer
              : this.options.promptUserMessageTemplate;
        
        systemRoleContent = [
          {
            type: 'text/fstring-template',
            text: systemPrompt
          }
        ];

        userRoleContent = [
          {
            type: 'text/fstring-template',
            text: userPrompt
          }
        ];
      } else { // Simple Prompt Instructions

        const systemPrompt = this.options.promptConditionalInstructionText
              ? this.options.llmPromptInstructionTextNoAnswer
              : this.options.llmPromptInstructionText;
        
        systemRoleContent = [
          {
            type: 'text',
            text: systemPrompt
          },
          {
            type: 'text/fstring-template',
            text: '{summaries}',
          }
        ];

        userRoleContent = [
          {
            type: 'text/fstring-template',
            text: '{question}'
          }    
        ];
      }

      
      return [
        {
          "role": "system",
          "content": systemRoleContent
        },
        {
          "role": "user",
          "content": userRoleContent
        }      
      ];
    },
    
    getMessageTemplates: function() {
      var systemRoleContent = [];
      var userRoleContent = [];

      // Advanced Prompt Instruction enabled
      if (this.options.promptUseTemplates === true) {
        systemRoleContent = [
          {
            type: 'text/fstring-template',
            text: this.options.llmPromptInstructionTemplate,
          }
        ];

        userRoleContent = [
          {
            type: 'text/fstring-template',
            text: this.options.promptUserMessageTemplate,
          }
        ];
      } else { // Simple Prompt Instructions
        systemRoleContent = [
          {
            type: 'text',
            text: this.options.llmPromptInstructionText,
          },
          {
            type: 'text/fstring-template',
            text: '{summaries}',
          }
        ];

        userRoleContent = [
          {
            type: 'text/fstring-template',
            text: '{question}'
          }    
        ];
      }
  
      return [
        {
          "role": "system",
          "content": systemRoleContent
        },
        {
          "role": "user",
          "content": userRoleContent
        }      
      ];
    },

    getEffectiveMessageTemplates: function() {
      if (this.searchAnswerModels && this.searchAnswerModels.length) {
        return this.getMessageTemplates();
      }

      return this.getMessageTemplatesNoResultAnswers();
    },

    getConversationInputPresetsJson: function() {
      if (!this.options.conversationInputPresetsJson) {
        return;
      }
      var presetJSON = this.options.conversationInputPresetsJson;
      if (! _.isObject(this.options.conversationInputPresetsJson)) {
        try {
          presetJSON = JSON.parse(this.options.conversationInputPresetsJson);
        } catch (e) {
          console.warn("Failed to parse conversationInputPresetsJson", e);
          presetJSON = {};
        }
      }
      return presetJSON;
    },    
    
    mergeWithPresetJson: function(existingPayload) {
      if (!this.options.conversationInputPresetsJson) {
        return existingPayload;
      }

      try {
        var presetJSON = this.options.conversationInputPresetsJson;
        if (! _.isObject(this.options.conversationInputPresetsJson)) {
          presetJSON = JSON.parse(this.options.conversationInputPresetsJson);
        }

        return ObjectUtil.merge(presetJSON, _.clone(existingPayload));
      } catch (e) {
        return existingPayload;
      }
    },

    // Hide the answers component in case of any API error
    hideComponentClientOnly: function() {
      if (this.options.application && !this.options.application.editor) {
        this.$el.addClass('hidden');
        this.$el.removeClass('mb-answers-container');
      }
    },

    showComponentClientOnly: function () {
      if (this.options.application && !this.options.application.editor) {
        if (!this.answerModel.input.validate()) {
          hideComponentClientOnly();
        }
        this.$el.removeClass('hidden');
        this.$el.addClass('mb-answers-container');
      }
    },

    handleStopGenerating: function() {
      this.answerModel.abortComputation();
    },

    // Handle action click on legacy mustache
    handleRegenerateAnswer: function() {
      this.answerModel.wiring.compute();
    },
    // Handle action click on legacy mustache
    handleCopyAnswer: function() {
      this.options.application.copyContentToClipboard({
        source: "answer",
        type: "text/html",
        content: this.answerModel.get('unparsed_generated_text')
      });
    },

    handleFeedback: function(e) {
      if (e && "keypress" === e.type && e.which !== 13 && e.which !== 27) {
        return true;
      }
      if (e) e.stopPropagation();

      Trace.feedback();
    },

    handleBatchRemove: function (models) {
      var that = this;
      if (this.options.aiGenerateText) {
        return that.$el.find(".mb-answer-item").empty();
      } else {
        return that.$el.find(".mb-answer-item").remove();
      }
    },
    
    removeOne: function (model) {
      var that = this;

      window.setTimeout(function () {
        _.each(that.groupEls, function (groupEl, key) {
          if (!groupEl.el.innerHTML && groupEl.parent.parentNode) {
            groupEl.parent.parentNode.removeChild(groupEl.parent);
          }
        });
      }, 0);
    },

    onAnswerComputed: function () {
      
      if (!this.options.useLegacyMustacheTemplate) {
        return;
      }
      const html = LitExports.html;
      const render = LitExports.render;
      const modelHolder = {model: this.answerModel};

      // Legacy template is rendered everytime, but sources are only availale at the end.
      const actionsTemplate = html`
         <insight-answer-actions .modelHolder=${modelHolder} .application=${this.options.application}></insight-answer-actions>
`;
      const targetEl = this.el.querySelector(".mb-answer-actions");
      
      if (targetEl) {
        targetEl.style = "diplay: block;"
        targetEl.innerHTML = "";
        render(actionsTemplate, targetEl);
      }

      const targetSourcesEl = this.el.querySelector(".mb-answer-sources");
      targetSourcesEl.classList.remove("hidden");

      let metadatakeys = null;
      if (this.options.metadatakeys) {
        metadatakeys = JSON.stringify(this.options.metadatakeys);
      }
      
      if (targetSourcesEl) {
        const sourcesTemplate = html`
          <insight-answer-sources .modelHolder=${modelHolder} .application=${this.options.application} metadatakeys=${metadatakeys}></insight-answer-sources>
        `;

        targetSourcesEl.innerHTML = "";
        render(sourcesTemplate, targetSourcesEl);
      }
    },
    
    render: function(model) {

      if (this.options.useLegacyMustacheTemplate) {
        const targetEl = this.el.querySelector(".mb-answer-actions");
        if (targetEl) {
          targetEl.style = "display: none;"
        }
        return;
      }
      
      const html = LitExports.html;
      const render = LitExports.render;
      const unsafeHTML = UnsafeHTML.unsafeHTML;
      const isComputing = model.get("computing");
      const question = model.get("question");
      const generatedText = model.get("generated_text");
      const _className = isComputing ? "streaming" : "streaming-complete";
      const template = html`
       <div class="mb-media-body mb-answer ${_className}">
        <div class="mb-answer-header">
            ${this.options.showQuestion ? html`<h1 class="mb-media-heading mb-custom-media-heading generated-answer-question">${question}</h1>` : null }
            <button class="mb-btn mb-action-stop-generating"> <i class="icon-stop"></i>${i18n("stop_responding")}</button>
        </div>
          <div class="mb-answer-text-container generated-answer-text">${unsafeHTML(generatedText)}</div>
      </div>`;

      
      if (!this.renderEl) {
        this.renderEl = document.createElement("div");
      }
      const el = this.el.querySelector("div.mb-answer-item");
      if (el) {
        el.style = "";
        el.insertBefore(this.renderEl,el.firstChild);
        el.classList.remove("hidden");
        el.parentElement?.classList?.remove("hidden");
        render(template, this.renderEl);
      } 
      this.applyAnswerLineLimit();
      return this;
    },

    updateAnswerText: function () {
      this.$el.find(".generated-answer-text").last().html(this.answerModel.output.get("generated_text"));
    },

    applyAnswerLineLimit: function () {
      let maxLines = this.options.lineLimit;
      if (maxLines == 0) {
        this.removeLineRestriction();
        return;
      }
      document.body.style.setProperty('--mindbreeze-answer-max-lines', maxLines);
      
      var $text = this.$el.find(".generated-answer-text");
      if(!$text[0]) {
        return;
      }

      let padding = parseFloat($text.css('padding-top')) + parseFloat($text.css('padding-bottom'));
      let totalHeight = $text[0].scrollHeight - padding;
      let lineHeight = parseFloat($text.css("line-height"));
      if(isNaN(lineHeight)) {
        lineHeight = parseFloat($text.css("font-size"))*1.2;
      }
      let lineCount = Math.round(totalHeight / lineHeight);

      if (lineCount > maxLines && !this.$el.hasClass("limit-answer-height") && !this.showMoreClicked) {
        this.$el.addClass("limit-answer-height");
        const icons = this.options.application.themeable.model.attributes.icons;
        const html = LitExports.html;
        const render = LitExports.render;
        let icon = WebComponentUtils.renderIcon(icons,"drop-down-arrow", {color: "currentColor", alt: i18n("icon")});
        const template = html`
          <div class="answer-show-more-container">
            <button class="answer-show-more">${i18n("show_more")} ${icon}</button>
          </div>
        `;
        const temp = document.createElement('div');
        render(template, temp);
        
        this.$el.find(".mb-media-body.mb-answer.streaming, .mb-media-body.mb-answer.streaming-complete").append(temp.firstElementChild);
      }
    },

    removeLineRestriction: function() {
      this.$el.find('.answer-show-more-container').remove();
      this.$el.removeClass("limit-answer-height");
      this.showMoreClicked = true; // boolean to stop the height to be re-checked after the button was clicked
    },

    _handleClick: function(e) { // listen for clicks on the root element because it remains stable during generation
      const btn = e.target.closest('.answer-show-more'); // check if answer-show-more button was the target
      if (!btn) return;

      e.preventDefault();
      this.removeLineRestriction(e);
    },

    remove: function () {
      this.model.unregisterComputationTaskProvider(this);
      if (this.model) this.model.off();

      ComponentBase.prototype.remove.apply(this, arguments);
      this.dispose();
      return this;
    },

    dispose: function () {
      this.answerModel && this.answerModel.destroy();
    }
  });

  AnswersTemplate.DEFAULT_TEMPLATE = DEFAULT_TEMPLATE && DEFAULT_TEMPLATE.default;
  AnswersTemplate.View = AnswersView;

  return AnswersTemplate;
});
